import { useEffect } from "react";

const CHUNK_RELOAD_KEY = "chunk_reload_attempted";
const CHUNK_RELOAD_TIMESTAMP_KEY = "chunk_reload_timestamp";
// Consider a reload "successful" if the page stays up for this many ms
const SUCCESS_THRESHOLD_MS = 3000;

function isChunkLoadError(error) {
  if (!error) return false;
  const msg = error?.message || "";
  return (
    error?.name === "ChunkLoadError" || msg?.includes("Failed to fetch dynamically imported module") ||
    msg?.includes("Importing a module script failed") ||
    msg?.includes("Unable to preload CSS") ||
    msg?.includes("error loading dynamically imported module") ||
    msg?.includes("dynamically imported module")
  );
}

function attemptChunkReload() {
  const reloadAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (!reloadAttempted) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    sessionStorage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, Date.now()?.toString());
    try {
      window.location?.reload(true);
    } catch (_) {
      window.location.href = window.location?.href;
    }
    return true;
  }
  return false;
}

/**
 * Detects dynamic import (chunk load) failures caused by stale cached assets
 * after a new deployment, and automatically reloads the page once to recover.
 */
const ChunkErrorHandler = () => {
  useEffect(() => {
    // Check if we previously attempted a reload. Only clear the flag after
    // the page has been stable for SUCCESS_THRESHOLD_MS — this confirms the
    // reload actually fixed the problem. Clearing immediately on mount was
    // the bug: it reset the flag before errors could even fire, allowing
    // infinite reload loops or preventing recovery.
    const alreadyAttempted = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (alreadyAttempted) {
      const reloadTime = parseInt(sessionStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY) || "0", 10);
      const elapsed = Date.now() - reloadTime;
      const remaining = Math.max(0, SUCCESS_THRESHOLD_MS - elapsed);

      const clearTimer = setTimeout(() => {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        sessionStorage.removeItem(CHUNK_RELOAD_TIMESTAMP_KEY);
      }, remaining);

      return () => clearTimeout(clearTimer);
    }
  }, []);

  useEffect(() => {
    const handleError = (event) => {
      const error = event?.error || event?.reason;
      if (!error) return;
      if (isChunkLoadError(error)) {
        event?.preventDefault?.();
        attemptChunkReload();
      }
    };

    // Catch unhandled promise rejections (dynamic import failures surface here)
    window.addEventListener("unhandledrejection", handleError);
    // Catch synchronous errors
    window.addEventListener("error", handleError);

    // Hook into ErrorBoundary's componentDidCatch — ErrorBoundary calls
    // window.__COMPONENT_ERROR__(error, errorInfo) for every caught error.
    const prevComponentError = window.__COMPONENT_ERROR__;
    window.__COMPONENT_ERROR__ = (error, errorInfo) => {
      if (isChunkLoadError(error)) {
        attemptChunkReload();
      }
      prevComponentError?.(error, errorInfo);
    };

    return () => {
      window.removeEventListener("unhandledrejection", handleError);
      window.removeEventListener("error", handleError);
      window.__COMPONENT_ERROR__ = prevComponentError;
    };
  }, []);

  return null;
};

export default ChunkErrorHandler;
